import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Link,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import { useEffect, useState } from 'react';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import { NumericField, OtaFooter } from '@ota/design-system';
import { ApiError } from '@ota/api-client';
import { login, sendOtp, verifyOtp } from '../api';
import { PublicHeader } from '../components/PublicHeader';

type Tab = 'email' | 'otp';
type OtpStep = 'mobile' | 'verify';

export function LoginPage() {
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const returnTo = search.get('returnTo') ?? '/';

  const [tab, setTab] = useState<Tab>('email');

  // ── Email / password ──────────────────────────────────────────────────────
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [emailBusy, setEmailBusy] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);

  const submitEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    setEmailError(null);
    setEmailBusy(true);
    try {
      await login({ email, password });
      navigate(returnTo);
    } catch (err) {
      setEmailError(err instanceof ApiError ? err.body?.message ?? err.message : 'Login failed');
    } finally {
      setEmailBusy(false);
    }
  };

  // ── Mobile OTP ────────────────────────────────────────────────────────────
  const [mobile, setMobile] = useState('');
  const [otp, setOtp] = useState('');
  const [otpStep, setOtpStep] = useState<OtpStep>('mobile');
  const [otpBusy, setOtpBusy] = useState(false);
  const [otpError, setOtpError] = useState<string | null>(null);
  const [otpSentMsg, setOtpSentMsg] = useState<string | null>(null);
  const [resendCooldown, setResendCooldown] = useState(0);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setTimeout(() => setResendCooldown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  const requestOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setOtpError(null);
    setOtpBusy(true);
    try {
      await sendOtp(mobile);
      setOtpSentMsg(`OTP sent to ${mobile}. Valid for 15 minutes.`);
      setResendCooldown(45);
      setOtpStep('verify');
    } catch (err) {
      setOtpError(err instanceof ApiError ? err.body?.message ?? err.message : 'Could not send OTP');
    } finally {
      setOtpBusy(false);
    }
  };

  const resendOtp = async () => {
    setOtpError(null);
    setOtpBusy(true);
    try {
      await sendOtp(mobile);
      setOtpSentMsg(`OTP resent to ${mobile}. Valid for 15 minutes.`);
      setResendCooldown(45);
    } catch (err) {
      setOtpError(err instanceof ApiError ? err.body?.message ?? err.message : 'Could not resend OTP');
    } finally {
      setOtpBusy(false);
    }
  };

  const submitOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setOtpError(null);
    setOtpBusy(true);
    try {
      await verifyOtp({ mobile, otp });
      navigate(returnTo);
    } catch (err) {
      setOtpError(err instanceof ApiError ? err.body?.message ?? err.message : 'OTP verification failed');
    } finally {
      setOtpBusy(false);
    }
  };

  const resetOtp = () => {
    setOtpStep('mobile');
    setOtp('');
    setOtpError(null);
    setOtpSentMsg(null);
    setResendCooldown(0);
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <PublicHeader />
      <Container maxWidth="sm" sx={{ py: { xs: 3, sm: 6, md: 8 }, flex: 1, px: { xs: 2, sm: 3 } }}>
        <Card elevation={2}>
          <CardContent sx={{ p: { xs: 2.5, sm: 4, md: 5 } }}>
            {/* Header */}
            <Stack spacing={0.5} sx={{ mb: 3, textAlign: 'center' }}>
              <Typography variant="overline" color="secondary.main">Welcome back</Typography>
              <Typography variant="h4" sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}>Sign in</Typography>
            </Stack>

            {/* Tabs */}
            <Tabs
              value={tab}
              onChange={(_, v) => { setTab(v); setEmailError(null); setOtpError(null); }}
              variant="fullWidth"
              sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
            >
              <Tab
                value="email"
                label="Email & Password"
                icon={<EmailIcon fontSize="small" />}
                iconPosition="start"
                sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, minHeight: 48 }}
              />
              <Tab
                value="otp"
                label="Mobile OTP"
                icon={<PhoneIcon fontSize="small" />}
                iconPosition="start"
                sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, minHeight: 48 }}
              />
            </Tabs>

            {/* ── Email tab ─────────────────────────────────── */}
            {tab === 'email' && (
              <form onSubmit={submitEmail}>
                <Stack spacing={2}>
                  <TextField
                    label="Email address"
                    type="email"
                    required
                    fullWidth
                    autoFocus
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    size="medium"
                  />
                  <TextField
                    label="Password"
                    type="password"
                    required
                    fullWidth
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                  {emailError && <Alert severity="error" sx={{ borderRadius: 1 }}>{emailError}</Alert>}
                  <Button
                    type="submit"
                    variant="contained"
                    color="secondary"
                    size="large"
                    fullWidth
                    disabled={emailBusy}
                    startIcon={emailBusy ? <CircularProgress size={18} color="inherit" /> : null}
                    sx={{ py: 1.5 }}
                  >
                    {emailBusy ? 'Signing in…' : 'Sign in'}
                  </Button>
                </Stack>
              </form>
            )}

            {/* ── OTP tab ───────────────────────────────────── */}
            {tab === 'otp' && (
              <>
                {otpStep === 'mobile' && (
                  <form onSubmit={requestOtp}>
                    <Stack spacing={2}>
                      <NumericField
                        label="Mobile number"
                        required
                        fullWidth
                        autoFocus
                        autoComplete="tel"
                        placeholder="10-digit mobile number"
                        inputProps={{ maxLength: 15 }}
                        value={mobile}
                        onChange={(e) => setMobile(e.target.value)}
                        helperText="Enter the mobile number registered with your account"
                      />
                      {otpError && <Alert severity="error">{otpError}</Alert>}
                      <Button
                        type="submit"
                        variant="contained"
                        color="secondary"
                        size="large"
                        fullWidth
                        disabled={otpBusy || mobile.length < 10}
                        startIcon={otpBusy ? <CircularProgress size={18} color="inherit" /> : null}
                        sx={{ py: 1.5 }}
                      >
                        {otpBusy ? 'Sending OTP…' : 'Send OTP'}
                      </Button>
                    </Stack>
                  </form>
                )}

                {otpStep === 'verify' && (
                  <form onSubmit={submitOtp}>
                    <Stack spacing={2}>
                      {otpSentMsg && (
                        <Alert severity="success" sx={{ borderRadius: 1 }}>{otpSentMsg}</Alert>
                      )}
                      <NumericField
                        label="Enter 6-digit OTP"
                        required
                        fullWidth
                        autoFocus
                        autoComplete="one-time-code"
                        inputProps={{ maxLength: 6 }}
                        value={otp}
                        onChange={(e) => setOtp(e.target.value)}
                        helperText={`Sent to ${mobile}`}
                      />
                      {otpError && <Alert severity="error">{otpError}</Alert>}
                      <Button
                        type="submit"
                        variant="contained"
                        color="secondary"
                        size="large"
                        fullWidth
                        disabled={otpBusy || otp.length !== 6}
                        startIcon={otpBusy ? <CircularProgress size={18} color="inherit" /> : null}
                        sx={{ py: 1.5 }}
                      >
                        {otpBusy ? 'Verifying…' : 'Verify & Sign in'}
                      </Button>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Button
                          variant="text"
                          size="small"
                          onClick={resendOtp}
                          disabled={otpBusy || resendCooldown > 0}
                          sx={{ color: resendCooldown > 0 ? 'text.disabled' : 'secondary.main' }}
                        >
                          {resendCooldown > 0 ? `Resend OTP in ${resendCooldown}s` : 'Resend OTP'}
                        </Button>
                        <Button
                          variant="text"
                          size="small"
                          onClick={resetOtp}
                          sx={{ color: 'text.secondary' }}
                        >
                          ← Change number
                        </Button>
                      </Stack>
                    </Stack>
                  </form>
                )}
              </>
            )}

            {/* Footer links */}
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ display: 'block', mt: 3, textAlign: 'center' }}
            >
              Don't have an account?{' '}
              <Link
                component={RouterLink}
                to={`/signup${returnTo !== '/' ? `?returnTo=${returnTo}` : ''}`}
              >
                Sign up
              </Link>
            </Typography>
          </CardContent>
        </Card>
      </Container>
      <OtaFooter />
    </Box>
  );
}
